/**
 * Floating combat text (WoW / MikScrollingBattleText style).
 *
 * - The human player's outgoing damage arcs up and out of the target, coloured by school. Crits are big
 *   and pop in with a scale overshoot. Heals are green.
 * - +castux (WoW scrolling layout): damage taken scrolls down in a column on the hero's left, heals
 *   received in a column on the hero's right (stacked, never on top of the hero); proc / transform
 *   gains ("+Sudden Death") rise in small gold text beside the hero. Periodic ticks are smaller and
 *   softer, overheal shows dimmed in parentheses, numbers abbreviate (12.4K / 1.2M) unless the
 *   "Full numbers" setting is on.
 * - AoE and periodic hits from one ability within MERGE_MS merge into one number (×N for multi-target AoE,
 *   anchored on the player's target when it was hit).
 * - A fixed pool of CAP DOM nodes; animation touches only transform/opacity. When the pool is full the
 *   oldest low-priority floater is recycled.
 *
 * The pure part (`FloaterModel`, formatting, colours, motion) has no DOM and is unit-tested.
 */
import { avoidPanels } from './avoid'; // +polish
import type { ContentRegistry, School, SimEvent, UnitId } from '@keystone/sim';
import type { Renderer, RenderUnit } from '../render/types';
import { type CombatTextSettings, combatTextSettings } from './combatTextSettings';
import { el } from './dom';
import { isProcRowAura } from './cluster/derive';

// ---------------------------------------------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------------------------------------------

/** 1-decimal under 100 of the unit ("12.4"), integer above ("124"). Returns null when it rounds up to 1000. */
function unitText(v: number, unit: string): string | null {
  const r1 = Math.round(v * 10) / 10;
  if (r1 < 100) return `${r1}${unit}`;
  const r0 = Math.round(v);
  return r0 < 1000 ? `${r0}${unit}` : null;
}

/** Combat-text number: 9876 → "9876", 12_400 → "12.4K", 124_000 → "124K", 1_240_000 → "1.2M", 3e9 → "3B". */
export function formatAmount(n: number): string {
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (Math.round(a) < 10_000) return `${sign}${Math.round(a)}`;
  return sign + (unitText(a / 1e3, 'K') ?? unitText(a / 1e6, 'M') ?? unitText(a / 1e9, 'B') ?? `${Math.round(a / 1e9)}B`);
}

/** +castux: full number with thousands separators ("12,400"), for the "Full numbers" setting. */
export function formatFull(n: number): string {
  const r = Math.round(Math.abs(n));
  const sign = n < 0 ? '-' : '';
  if (r < 1000) return `${sign}${r}`;
  let out = '';
  let v = r;
  while (v >= 1000) {
    const g = v % 1000;
    out = `,${g < 10 ? '00' : g < 100 ? '0' : ''}${g}${out}`;
    v = Math.floor(v / 1000);
  }
  return `${sign}${v}${out}`;
}

/**
 * Damage colour by school (WoW-like, tuned against render/palette SCHOOL_COLOR for contrast on the
 * dark floor). Physical is white, crits go yellow; magic schools keep their hue when they crit.
 * Nature is the Stormcaller's lightning: blue-violet.
 */
export const SCHOOL_COLOR: Readonly<Record<School, string>> = Object.freeze({
  physical: '#ffffff',
  fire: '#ff8a1f',
  nature: '#9fb0ff',
  frost: '#7cc8ff',
  holy: '#ffd23a',
  shadow: '#b77bff',
  arcane: '#ff8af2',
});
export const PHYSICAL_CRIT_COLOR = '#ffe14a';
export const COLOR = Object.freeze({
  incoming: '#ff3b30',
  heal: '#3dff6e',
  absorb: '#a3abbb',
  interrupt: '#2ee6ff',
  avoid: '#e6e9f0',
  /** +castux: proc / transform gains on the player ("+Sudden Death"). */
  proc: '#ffd56a',
});

export function damageColor(school: School | null | undefined, crit: boolean): string {
  if (!school) return crit ? PHYSICAL_CRIT_COLOR : SCHOOL_COLOR.physical;
  if (school === 'physical' && crit) return PHYSICAL_CRIT_COLOR;
  return SCHOOL_COLOR[school] ?? SCHOOL_COLOR.physical;
}

const AVOID_TEXT: Partial<Record<string, string>> = { miss: 'Miss', dodge: 'Dodge', parry: 'Parry', immune: 'Immune', block: 'Block', absorb: '(Absorbed)' };

export const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;
export const easeOutQuad = (t: number): number => 1 - (1 - t) * (1 - t);
/** Back-out: overshoots past 1 then settles. */
export const easeOutBack = (t: number): number => {
  const c1 = 1.9;
  const c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
};

// ---------------------------------------------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------------------------------------------

export type FloaterKind = 'out' | 'in' | 'heal' | 'absorb' | 'kick' | 'avoid' | 'reward' | 'proc';

/** Kill reward pops ("+N XP" violet, "+N gold" with a coin), smaller than damage numbers; bosses pop bigger. */
export const REWARD_COLOR = Object.freeze({ xp: '#b98cff', gold: '#ffd24a' });
export const REWARD_LIFE_MS = 1700;
export const REWARD_BOSS_LIFE_MS = 2600;

/** One rewarded kill to pop (progression's KillRewardEntry fits). */
export interface RewardPop {
  unitId: UnitId;
  pos: { x: number; y: number };
  xp: number;
  gold: number;
  boss: boolean;
}

export const CAP = 40;
export const MERGE_MS = 150;
const MAX_TARGETS = 32;
/** Crit pop: 1.6× → 1.0 (with a small undershoot) over this long. */
export const CRIT_POP_MS = 120;
export const CRIT_POP_FROM = 1.6;
/** +impact: a crit this many times the player's recent average hit is "huge": bigger, pops harder, tilts. */
export const HUGE_REL = 1.8;
export const HUGE_POP_FROM = 1.85;
/** +impact: after the pop, crits bounce once (scale +BOUNCE_AMP over BOUNCE_MS). */
export const BOUNCE_MS = 170;
export const BOUNCE_AMP = 0.08;
/** +impact: crits landing on the same target within this window stack upward instead of overlapping. */
export const STACK_MS = 320;
const STACK_PX = 30;
/** +castux: numbers in the hero's incoming / heal columns spawned within this window stack one line apart. */
export const COLUMN_MS = 420;
export const COLUMN_LINE_PX = 24;
/** +castux: periodic ticks are drawn at this opacity (softer than direct hits). */
export const TICK_ALPHA = 0.82;
/** +castux: horizontal offset (px) of the hero's incoming (left) and heal (right) columns. */
export const COLUMN_X_PX = 86;
export interface FloaterSlot {
  active: boolean;
  /** Spawn order (for eviction ties). */
  seq: number;
  kind: FloaterKind;
  crit: boolean;
  party: boolean;
  /** Incoming = anchored on the player, scrolls on the player's side. */
  sourceId: UnitId;
  abilityId: string;
  /** The unit the floater is anchored to. */
  anchorId: UnitId;
  aoe: boolean;
  tick: boolean;
  school: School | null;
  born: number;
  /** Last merge (re-pulse). */
  bumped: number;
  life: number;
  amount: number;
  absorbed: number;
  blocked: number;
  /** +castux: overheal (shown dimmed in parentheses). */
  overheal: number;
  /** +castux: a heal on the player (scrolls in the right-hand column). */
  self: boolean;
  hits: number;
  /** Distinct targets hit (AoE merges). Reused array. */
  readonly targets: UnitId[];
  text: string;
  suffix: string;
  color: string;
  /** Eviction priority: lower is dropped first. */
  weight: number;
  /** Size class for the DOM ('s', 'm', 'l', 'xl', 'xxl' for huge crits). */
  size: 's' | 'm' | 'l' | 'xl' | 'xxl';
  /** +impact: a crit far above the player's average hit (bigger, harder pop, settles from a tilt). */
  huge: boolean;
  /** +autoattack: a white hit (auto-attack / Auto Shot): smaller plain white numbers, crits bigger. */
  white: boolean;
  /** +impact: starting tilt (degrees) that settles to 0 during the pop. */
  tilt: number;
  /** Extra CSS classes (reward pops: 'ct-xp' / 'ct-gold', 'ct-boss'). */
  variant: string;
  /** Arc direction (-1 or 1), horizontal travel and spawn jitter (px). */
  dir: number;
  spread: number;
  jx: number;
  jy: number;
  /** Text/class changed since the DOM last saw it. */
  dirty: boolean;
  /** Last known anchor world position (kept when the unit despawns). */
  wx: number;
  wy: number;
  wh: number;
  hasPos: boolean;
}

function emptySlot(): FloaterSlot {
  return {
    active: false, seq: 0, kind: 'out', crit: false, party: false, sourceId: '', abilityId: '', anchorId: '', aoe: false, tick: false,
    school: null, born: 0, bumped: 0, life: 1, amount: 0, absorbed: 0, blocked: 0, overheal: 0, self: false, hits: 0, targets: [], text: '', suffix: '', color: '',
    weight: 0, size: 'm', huge: false, white: false, tilt: 0, variant: '', dir: 1, spread: 0, jx: 0, jy: 0, dirty: false, wx: 0, wy: 0, wh: 0, hasPos: false,
  };
}

export interface IngestContext {
  playerId: UnitId | null;
  now: number;
  showParty: boolean;
  /** Is this unit a party member (player character on the player's side)? */
  isParty(id: UnitId): boolean;
  /** +castux: display name of an aura worth a "+Name" gain pop (procs / transforms), else null. */
  procName?(auraId: string): string | null;
}

/** Output of `motion()` (reused; no allocation). */
export interface Motion {
  x: number;
  y: number;
  scale: number;
  alpha: number;
  /** +impact: rotation in degrees (crit tilt settling). Optional for older callers. */
  rot?: number;
}

export class FloaterModel {
  readonly slots: FloaterSlot[] = [];
  /** The player's current target; AoE merges anchor there when it was hit. */
  primaryTarget: UnitId | null = null;
  /** Fallback when the HUD does not tell us the target: the last target of a direct single-target hit. */
  private lastDirectTarget: UnitId | null = null;
  critPop = true;
  /** +castux: full numbers instead of 12.4K (combat text setting). */
  fullNumbers = false;
  /** Graphics "effects intensity" (0.2..1.5): scales the pop/bump punch (capped at 1×, never shrinks text). */
  fxIntensity = 1;
  private seq = 0;
  private dirToggle = 1;
  /** +impact: running average of the player's own direct, non-crit hits (0 = none yet). */
  private avgHit = 0;

  constructor(
    readonly cap = CAP,
    private readonly rng: () => number = Math.random,
  ) {
    for (let i = 0; i < cap; i++) this.slots.push(emptySlot());
  }

  get activeCount(): number {
    let n = 0;
    for (const s of this.slots) if (s.active) n++;
    return n;
  }

  private target(): UnitId | null {
    return this.primaryTarget ?? this.lastDirectTarget;
  }

  /** +castux: a number as the settings want it (abbreviated or full). */
  fmt(n: number): string {
    return this.fullNumbers ? formatFull(n) : formatAmount(n);
  }

  ingest(events: readonly SimEvent[], ctx: IngestContext): void {
    const me = ctx.playerId;
    if (!me) return;
    for (const e of events) {
      if (e.type === 'damage') {
        if (e.targetID === me) {
          this.damageIn(e, ctx.now);
          continue;
        }
        const mine = e.sourceID === me;
        const party = !mine && ctx.showParty && ctx.isParty(e.sourceID) && !ctx.isParty(e.targetID);
        if (mine || party) this.damageOut(e, party, ctx.now);
      } else if (e.type === 'heal') {
        // +castux: a fully overhealed direct heal still shows "(N)"; overhealed ticks stay quiet.
        if (e.amount <= 0 && (e.tick || !(e.overheal > 0) || e.sourceID !== me)) continue;
        const mine = e.sourceID === me;
        const party = !mine && ctx.showParty && ctx.isParty(e.sourceID);
        if (mine || party || e.targetID === me) this.heal(e.sourceID, e.targetID, e.abilityGameID, e.amount, e.overheal ?? 0, e.hitType === 'crit', !!e.tick, party && e.targetID !== me, ctx.now, e.targetID === me);
      } else if (e.type === 'applybuff' && e.targetID === me && ctx.procName) {
        // +castux: proc / transform gains ("+Sudden Death"), small gold text beside the hero.
        const name = ctx.procName(e.abilityGameID);
        if (name) this.proc(e.abilityGameID, name, me, ctx.now);
      } else if (e.type === 'interrupt') {
        const mine = e.sourceID === me;
        const party = !mine && ctx.showParty && ctx.isParty(e.sourceID);
        if (mine || party) {
          const s = this.spawn('kick', e.sourceID, e.abilityGameID, e.targetID, party, ctx.now);
          s.text = 'Interrupted!';
          s.color = COLOR.interrupt;
          s.size = party ? 's' : 'l';
          s.weight = party ? 0 : 4;
          s.life = 1400;
        }
      }
    }
  }

  private damageOut(e: Extract<SimEvent, { type: 'damage' }>, party: boolean, now: number): void {
    const crit = e.hitType === 'crit';
    const avoid = AVOID_TEXT[e.hitType];
    const total = e.amount + e.absorbed;
    if (avoid && total <= 0) {
      const s = this.spawn('avoid', e.sourceID, e.abilityGameID, e.targetID, party, now);
      s.text = avoid;
      s.school = e.school;
      s.aoe = e.isAoE;
      s.tick = !!e.tick;
      s.color = e.hitType === 'absorb' ? COLOR.absorb : COLOR.avoid;
      s.kind = e.hitType === 'absorb' ? 'absorb' : 'avoid';
      s.size = 's';
      s.weight = party ? 0 : 1;
      s.life = 900;
      return;
    }
    if (total <= 0) return;
    if (!party && !e.isAoE && !e.tick) this.lastDirectTarget = e.targetID;
    // +impact: track the player's typical hit; a crit far above it is "huge".
    let huge = false;
    if (!party && !e.tick && !e.swing) {
      if (crit) huge = this.avgHit > 0 && total >= HUGE_REL * this.avgHit;
      else this.avgHit = this.avgHit > 0 ? this.avgHit + (total - this.avgHit) * 0.2 : total;
    }

    const mergeable = e.isAoE || !!e.tick;
    let s = mergeable ? this.findMerge('out', e.sourceID, e.abilityGameID, e.targetID, e.isAoE, now) : null;
    if (s) {
      s.bumped = now;
    } else {
      s = this.spawn('out', e.sourceID, e.abilityGameID, e.targetID, party, now);
      s.aoe = e.isAoE;
      s.tick = !!e.tick;
      s.school = e.school;
      // +impact: numbers landing on one target in quick succession stack upward instead of piling
      // on each other (crits step further: they are bigger).
      if (!party) {
        let n = 0;
        for (const o of this.slots) if (o !== s && o.active && o.kind === 'out' && !o.party && o.anchorId === e.targetID && now - o.born < STACK_MS) n++;
        s.jy -= (crit ? STACK_PX : STACK_PX * 0.7) * Math.min(3, n);
        if (n > 0) s.jx *= 0.4;
      }
    }
    if (huge) s.huge = true;
    if (e.swing) s.white = true; // +autoattack
    if (crit && !party) s.tilt = (this.rng() < 0.5 ? -1 : 1) * (s.huge ? 7 : 4);
    s.amount += e.amount;
    s.absorbed += e.absorbed;
    s.hits++;
    s.crit ||= crit;
    if (!s.targets.includes(e.targetID) && s.targets.length < MAX_TARGETS) s.targets.push(e.targetID);
    // Multi-target AoE: show the merged number on the player's target when it was hit.
    const primary = this.target();
    if (s.aoe && primary && s.anchorId !== primary && e.targetID === primary) s.anchorId = primary;
    this.styleOut(s);
  }

  private styleOut(s: FloaterSlot): void {
    s.color = damageColor(s.school, s.crit);
    if (s.amount <= 0) {
      s.kind = 'absorb';
      s.text = '(Absorbed)';
      s.color = COLOR.absorb;
      s.suffix = '';
    } else {
      s.kind = 'out';
      s.text = s.crit ? `${this.fmt(s.amount)}!` : this.fmt(s.amount);
      const n = s.targets.length;
      s.suffix = n > 1 ? `×${n}` : '';
      if (s.absorbed > 0) s.suffix += `${s.suffix ? ' ' : ''}(${this.fmt(s.absorbed)} Absorbed)`;
    }
    // +castux: periodic ticks (DoTs, zones) one size down and softer (variant ct-dot).
    const periodic = s.tick && !s.party;
    s.variant = periodic ? 'ct-dot' : '';
    s.size = s.party ? (s.crit ? 'm' : 's') : s.crit ? (s.huge ? 'xxl' : periodic ? 'l' : 'xl') : periodic ? 's' : 'l';
    s.weight = s.party ? 0 : s.crit ? (s.huge ? 5 : 4) : 2;
    // +autoattack: WoW's white numbers: plain white, a size down; a white crit pops to full size.
    if (s.white && s.amount > 0) {
      s.color = s.crit ? PHYSICAL_CRIT_COLOR : SCHOOL_COLOR.physical;
      s.variant = 'ct-white';
      if (!s.party) s.size = s.crit ? 'l' : 'm';
      s.weight = s.crit ? 3 : 1;
    }
    s.life = s.party ? 850 : s.crit ? (s.huge ? 1650 : 1400) : 1050;
    s.dirty = true;
  }

  private damageIn(e: Extract<SimEvent, { type: 'damage' }>, now: number): void {
    const avoid = AVOID_TEXT[e.hitType];
    if (avoid && e.amount + e.absorbed <= 0) {
      const s = this.spawn('in', e.sourceID, e.abilityGameID, e.targetID, false, now);
      this.column(s, now);
      s.text = avoid;
      s.color = e.hitType === 'absorb' ? COLOR.absorb : COLOR.avoid;
      s.size = 's';
      s.weight = 1;
      s.life = 950;
      return;
    }
    if (e.amount + e.absorbed <= 0) return;
    let s = this.findMerge('in', null, e.abilityGameID, e.targetID, false, now);
    if (s) s.bumped = now;
    else {
      s = this.spawn('in', e.sourceID, e.abilityGameID, e.targetID, false, now);
      s.school = e.school;
      this.column(s, now);
    }
    s.tick = s.tick || !!e.tick;
    s.amount += e.amount;
    s.absorbed += e.absorbed;
    s.blocked += e.blocked ?? 0;
    s.hits++;
    s.crit ||= e.hitType === 'crit';
    if (s.amount <= 0) {
      s.text = '(Absorbed)';
      s.color = COLOR.absorb;
      s.suffix = '';
    } else {
      s.text = `-${this.fmt(s.amount)}${s.crit ? '!' : ''}`;
      s.color = COLOR.incoming;
      let suffix = s.hits > 1 ? `×${s.hits}` : '';
      if (s.absorbed > 0) suffix += `${suffix ? ' ' : ''}(${this.fmt(s.absorbed)} Absorbed)`;
      if (s.blocked > 0) suffix += `${suffix ? ' ' : ''}(${this.fmt(s.blocked)} Blocked)`;
      s.suffix = suffix;
    }
    s.size = s.crit ? 'l' : s.tick ? 's' : 'm';
    s.variant = s.tick && !s.crit ? 'ct-dot' : '';
    s.weight = 3;
    s.life = 1250;
    s.dirty = true;
  }

  /**
   * +castux: the hero's incoming / heal columns: a number spawned while others of its column are
   * still near the top starts one line higher (they all scroll down at the same speed, so they
   * stay apart), with no sideways scatter.
   */
  private column(s: FloaterSlot, now: number): void {
    let n = 0;
    const heal = s.kind === 'heal';
    for (const o of this.slots) {
      if (o === s || !o.active || now - o.born >= COLUMN_MS) continue;
      if (heal ? o.kind === 'heal' && o.self : o.kind === 'in') n++;
    }
    s.jx = (this.rng() - 0.5) * 6;
    s.jy = -COLUMN_LINE_PX * Math.min(4, n);
  }

  /** +castux: "+Sudden Death": a proc / transform the player gained (small gold, beside the hero). */
  private proc(auraId: string, name: string, me: UnitId, now: number): void {
    // One pop per gain: a re-proc within the merge window just bumps the existing one.
    for (const o of this.slots) {
      if (o.active && o.kind === 'proc' && o.abilityId === auraId && now - o.born < 600) {
        o.bumped = now;
        return;
      }
    }
    const s = this.spawn('proc', me, auraId, me, false, now);
    s.text = `+${name}`;
    s.color = COLOR.proc;
    s.size = 's';
    s.weight = 3;
    s.life = 1500;
    let n = 0;
    for (const o of this.slots) if (o !== s && o.active && o.kind === 'proc' && now - o.born < 500) n++;
    s.jx = 0;
    s.jy = -18 * Math.min(3, n);
    s.dirty = true;
  }

  private heal(src: UnitId, tgt: UnitId, ability: string, amount: number, overheal: number, crit: boolean, tick: boolean, party: boolean, now: number, onMe: boolean): void {
    let s = tick ? this.findMerge('heal', src, ability, tgt, false, now) : null;
    if (s) s.bumped = now;
    else {
      s = this.spawn('heal', src, ability, tgt, party, now);
      s.self = onMe;
      s.tick = tick;
      // +castux: heals on the player scroll down a column on the hero's right.
      if (onMe) this.column(s, now);
    }
    s.amount += amount;
    s.overheal += overheal;
    s.hits++;
    s.crit ||= crit;
    // +castux: overheal dimmed in parentheses ("+1.2K (340)"); a full overheal is just "(340)".
    s.text = s.amount > 0 ? `+${this.fmt(s.amount)}${s.crit ? '!' : ''}` : `(${this.fmt(s.overheal)})`;
    s.suffix = s.amount > 0 && s.overheal > 0 ? `(${this.fmt(s.overheal)})` : '';
    s.color = s.amount > 0 ? COLOR.heal : COLOR.absorb;
    const periodic = s.tick && !party;
    s.variant = periodic ? 'ct-dot' : s.amount <= 0 ? 'ct-overheal' : '';
    s.size = party || (periodic && !s.crit) || s.amount <= 0 ? 's' : s.crit ? 'l' : 'm';
    s.weight = party ? 0 : s.crit ? 3 : 2;
    s.life = party ? 850 : s.crit ? 1300 : 1100;
    if (onMe) s.dir = 1;
    s.dirty = true;
  }

  /**
   * Kill rewards rising from the corpse: "+N XP" then "+N gold" just below it. Shown for any viewer
   * (also when spectating). Anchored at the death position so a despawned corpse still shows them.
   */
  reward(r: RewardPop, now: number): void {
    const lines: [keyof typeof REWARD_COLOR, number, string][] = [
      ['xp', r.xp, 'XP'],
      ['gold', r.gold, 'gold'],
    ];
    let row = 0;
    for (const [kind, amount, label] of lines) {
      if (amount <= 0) continue;
      const s = this.spawn('reward', '', `reward:${kind}`, r.unitId, false, now);
      s.text = `+${this.fmt(amount)}`;
      s.suffix = label;
      s.color = REWARD_COLOR[kind];
      s.variant = `ct-${kind}${r.boss ? ' ct-boss' : ''}`;
      s.size = r.boss ? 'm' : 's';
      s.crit = false;
      s.weight = r.boss ? 4 : 3;
      s.life = r.boss ? REWARD_BOSS_LIFE_MS : REWARD_LIFE_MS;
      // Stacked lines, no random sideways scatter (they read as a small receipt).
      s.jx = (this.rng() - 0.5) * 10;
      s.jy = row * (r.boss ? 24 : 17);
      s.dir = 1;
      s.wx = r.pos.x;
      s.wy = r.pos.y;
      s.wh = 1.6;
      s.hasPos = true;
      s.dirty = true;
      row++;
    }
  }

  /** Same kind + ability (+ source when given) on the same anchor (or any target for AoE) within MERGE_MS. */
  private findMerge(kind: FloaterKind, src: UnitId | null, ability: string, tgt: UnitId, aoe: boolean, now: number): FloaterSlot | null {
    for (const s of this.slots) {
      if (!s.active || now - s.born > MERGE_MS || s.abilityId !== ability) continue;
      if (s.kind !== kind && !(kind === 'out' && s.kind === 'absorb')) continue;
      if (src !== null && s.sourceId !== src) continue;
      if (aoe ? s.aoe : s.anchorId === tgt && !s.aoe) return s;
    }
    return null;
  }

  /** Take a free slot, or recycle the lowest-weight (then oldest) one. */
  private spawn(kind: FloaterKind, src: UnitId, ability: string, anchor: UnitId, party: boolean, now: number): FloaterSlot {
    let pick: FloaterSlot | null = null;
    for (const s of this.slots) {
      if (!s.active) {
        pick = s;
        break;
      }
      if (!pick || s.weight < pick.weight || (s.weight === pick.weight && s.seq < pick.seq)) pick = s;
    }
    const s = pick!;
    s.active = true;
    s.seq = ++this.seq;
    s.kind = kind;
    s.crit = false;
    s.party = party;
    s.sourceId = src;
    s.abilityId = ability;
    s.anchorId = anchor;
    s.aoe = false;
    s.tick = false;
    s.school = null;
    s.born = now;
    s.bumped = -Infinity;
    s.amount = 0;
    s.absorbed = 0;
    s.blocked = 0;
    s.overheal = 0;
    s.self = false;
    s.hits = 0;
    s.targets.length = 0;
    s.text = '';
    s.suffix = '';
    s.variant = '';
    s.huge = false;
    s.white = false;
    s.tilt = 0;
    s.hasPos = false;
    s.dirty = true;
    this.dirToggle = -this.dirToggle;
    s.dir = kind === 'in' ? -1 : this.dirToggle;
    s.spread = 24 + this.rng() * 40;
    s.jx = (this.rng() - 0.5) * 28;
    s.jy = (this.rng() - 0.5) * 16;
    return s;
  }

  /** Free slots past their lifetime. */
  expire(now: number): void {
    for (const s of this.slots) if (s.active && now - s.born >= s.life) s.active = false;
  }

  clear(): void {
    for (const s of this.slots) s.active = false;
  }

  /**
   * Screen-space offset (px, relative to the anchor's head), scale and alpha for a slot at `now`.
   * Writes into `out` (no allocation).
   */
  motion(s: FloaterSlot, now: number, out: Motion): Motion {
    const age = now - s.born;
    const t = Math.min(1, Math.max(0, age / s.life));
    let x = s.jx;
    let y = s.jy;
    let scale = 1;
    // Pop-in: crits overshoot 1.6× → 1.0 in CRIT_POP_MS; everything else gets a smaller punch.
    const big = s.crit && !s.party;
    const popMs = big ? CRIT_POP_MS : 90;
    const punch = Math.min(1, Math.max(0, this.fxIntensity));
    const reward = s.kind === 'reward';
    const popBase = big ? (this.critPop ? (s.huge ? HUGE_POP_FROM : CRIT_POP_FROM) : 1) : s.kind === 'kick' ? 1.4 : reward ? (s.size === 'm' ? 1.5 : 1.3) : s.party ? 1 : 1.25;
    const popFrom = 1 + (popBase - 1) * punch;
    let rot = 0;
    if (age < popMs) scale = popFrom + (1 - popFrom) * easeOutBack(age / popMs);
    else if (big && this.critPop && age < popMs + BOUNCE_MS) {
      // +impact: one settling bounce after the pop (bigger for huge crits).
      scale = 1 + BOUNCE_AMP * (s.huge ? 1.5 : 1) * punch * Math.sin((Math.PI * (age - popMs)) / BOUNCE_MS);
    }
    if (big && this.critPop && s.tilt !== 0) rot = s.tilt * punch * (1 - easeOutCubic(Math.min(1, age / (popMs + BOUNCE_MS))));
    // Merge bump: a quick 1.12× pulse when more hits land in.
    const sinceBump = now - s.bumped;
    if (sinceBump >= 0 && sinceBump < 110) scale *= 1 + 0.12 * punch * (1 - sinceBump / 110);

    let fadeFrom = 0.62;
    switch (s.kind) {
      case 'out':
      case 'absorb':
        if (s.crit) {
          // Hang briefly, then float up with a little sideways drift.
          x += s.dir * s.spread * 0.35 * easeOutCubic(t);
          y += -12 - 64 * easeOutQuad(Math.max(0, (age - 160) / (s.life - 160)));
          fadeFrom = 0.7;
        } else {
          // Ballistic arc up and out.
          const sec = age / 1000;
          x += s.dir * s.spread * easeOutCubic(t) * 1.6;
          y += -14 - 170 * sec + 95 * sec * sec;
        }
        break;
      case 'in':
        // +castux: the left-hand column beside the hero, scrolling down.
        x += -COLUMN_X_PX;
        y += -6 + 92 * easeOutQuad(t);
        break;
      case 'heal':
        if (s.self) {
          // +castux: heals received: the right-hand column, scrolling down.
          x += COLUMN_X_PX;
          y += -6 + 92 * easeOutQuad(t);
        } else {
          x += s.dir * (36 + 10 * t);
          y += -10 - 70 * easeOutCubic(t);
        }
        break;
      case 'proc':
        // +castux: a small gold gain rising beside the hero's head.
        x += 34;
        y += -8 - 40 * easeOutCubic(t);
        fadeFrom = 0.7;
        break;
      case 'kick':
        x *= 0.3;
        y += -26 - 46 * easeOutCubic(t);
        fadeFrom = 0.7;
        break;
      case 'avoid':
        x += s.dir * 20;
        y += -8 - 50 * easeOutQuad(t);
        break;
      case 'reward':
        // A slow, straight rise off the corpse; hangs a moment before fading.
        y += -4 - (s.size === 'm' ? 70 : 52) * easeOutCubic(t);
        fadeFrom = 0.72;
        break;
    }
    out.x = x;
    out.y = y;
    out.scale = scale;
    out.rot = rot;
    out.alpha = (t < fadeFrom ? 1 : 1 - ((t - fadeFrom) / (1 - fadeFrom)) ** 1.5) * (s.party ? 0.8 : 1) * (s.variant === 'ct-dot' ? TICK_ALPHA : 1);
    return out;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// DOM view
// ---------------------------------------------------------------------------------------------------------------

interface NodeView {
  root: HTMLElement;
  value: HTMLElement;
  suffix: HTMLElement;
  shown: boolean;
}

export class FloatingText {
  readonly root: HTMLElement;
  readonly model = new FloaterModel();
  private readonly nodes: NodeView[] = [];
  private readonly m: Motion = { x: 0, y: 0, scale: 1, alpha: 1, rot: 0 };
  private settings: Readonly<CombatTextSettings> = combatTextSettings.get();
  private units: ReadonlyMap<UnitId, RenderUnit> | null = null;
  /** +castux: aura id → "+Name" pop text for procs / transforms (null = no pop), cached per content. */
  private content: ContentRegistry | null = null;
  private readonly procNames = new Map<string, string | null>();
  private readonly ctx: IngestContext = {
    playerId: null,
    now: 0,
    showParty: false,
    isParty: (id) => this.units?.get(id)?.faction === 'party',
    procName: (auraId) => {
      let n = this.procNames.get(auraId);
      if (n === undefined) {
        // Same rule as the cluster's proc row (cluster/derive.ts isProcRowAura).
        const def = this.content?.aura(auraId);
        n = def && isProcRowAura(def) ? def.name : null;
        if (this.content) this.procNames.set(auraId, n);
      }
      return n;
    },
  };

  /** +castux: content for the proc-gain names (call when it changes; cheap when it doesn't). */
  setContent(content: ContentRegistry): void {
    if (content === this.content) return;
    this.content = content;
    this.procNames.clear();
  }

  constructor(parent: HTMLElement) {
    this.root = el('div', 'floaters', parent);
    for (let i = 0; i < this.model.cap; i++) {
      const root = el('div', 'ct', this.root);
      const value = el('span', 'ct-v', root);
      const suffix = el('span', 'ct-x', root);
      root.style.display = 'none';
      this.nodes.push({ root, value, suffix, shown: false });
    }
    this.applySettings(combatTextSettings.get());
  }

  /** Settings are polled (the store returns the same object until it changes), so nothing needs unsubscribing. */
  private applySettings(s: Readonly<CombatTextSettings>): void {
    this.settings = s;
    this.model.critPop = s.critPop;
    this.model.fullNumbers = s.fullNumbers; // +castux
    this.root.style.setProperty('--ct-scale', String(s.scale));
  }

  /** Kill reward pops (see FloaterModel.reward). */
  reward(r: RewardPop, now: number): void {
    this.model.reward(r, now);
  }

  ingest(events: readonly SimEvent[], playerId: UnitId | null, now: number): void {
    this.ctx.playerId = playerId;
    this.ctx.now = now;
    const cur = combatTextSettings.get();
    if (cur !== this.settings) this.applySettings(cur);
    this.ctx.showParty = cur.showParty;
    this.model.ingest(events, this.ctx);
  }

  /** `selectedId` (optional): the player's target, used to anchor merged AoE numbers. */
  update(now: number, units: ReadonlyMap<UnitId, RenderUnit>, renderer: Renderer, selectedId?: UnitId | null): void {
    this.units = units;
    const cur = combatTextSettings.get();
    if (cur !== this.settings) this.applySettings(cur);
    if (selectedId !== undefined) this.model.primaryTarget = selectedId;
    this.model.expire(now);
    const slots = this.model.slots;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i]!;
      const n = this.nodes[i]!;
      if (!s.active) {
        if (n.shown) {
          n.shown = false;
          n.root.style.display = 'none';
        }
        continue;
      }
      if (s.dirty) {
        s.dirty = false;
        n.root.className = `ct ct-${s.kind} ct-${s.size}${s.crit ? ' ct-crit' : ''}${s.huge ? ' ct-huge' : ''}${s.party ? ' ct-party' : ''}${s.variant ? ` ${s.variant}` : ''}`;
        n.root.style.setProperty('--ct-c', s.color);
        n.value.textContent = s.text;
        n.suffix.textContent = s.suffix;
      }
      const u = units.get(s.anchorId);
      if (u) {
        s.wx = u.pos.x;
        s.wy = u.pos.y;
        s.wh = renderer.unitHeight(u.id);
        s.hasPos = true;
      }
      if (!s.hasPos) {
        s.active = false;
        if (n.shown) {
          n.shown = false;
          n.root.style.display = 'none';
        }
        continue;
      }
      this.pos.x = s.wx;
      this.pos.y = s.wy;
      // +castux: the incoming / heal-received columns sit at mid-body beside the hero; proc gains by the head.
      const sp = renderer.project(this.pos, s.kind === 'in' || s.self ? s.wh * 0.6 : s.kind === 'proc' ? s.wh + 0.3 : s.wh + 0.9);
      const m = this.model.motion(s, now, this.m);
      if (!n.shown) {
        n.shown = true;
        n.root.style.display = '';
      }
      const rot = m.rot ? ` rotate(${m.rot.toFixed(2)}deg)` : '';
      // +polish: never over the tracker / target frame / action bar.
      AV.x = sp.x + m.x;
      AV.y = sp.y + m.y;
      avoidPanels(AV, (14 + s.text.length * 11) * m.scale, 26 * m.scale, now);
      n.root.style.transform = `translate3d(${AV.x.toFixed(1)}px,${AV.y.toFixed(1)}px,0) translate(-50%,-50%) scale(${m.scale.toFixed(3)})${rot}`;
      n.root.style.opacity = sp.visible ? m.alpha.toFixed(3) : '0';
    }
  }

  private readonly pos = { x: 0, y: 0 };

  clear(): void {
    this.model.clear();
    for (const n of this.nodes) {
      n.shown = false;
      n.root.style.display = 'none';
    }
  }

  dispose(): void {
    this.clear();
    this.root.remove();
  }
}

const AV = { x: 0, y: 0 };
